import { useState, useRef, useEffect } from "react";
import "./App.css";
import Recorder from "recorder-js";
import { IoChatbox } from "react-icons/io5";
import { TiMicrophoneOutline } from "react-icons/ti";
import { LuSendHorizontal, LuX, LuPencil } from "react-icons/lu";
import { HiOutlineSpeakerWave } from "react-icons/hi2";
import { FaRegCheckCircle } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { CiStop1 } from "react-icons/ci";
import { useMsal } from "@azure/msal-react";
import { getLoginRequest, getTokenRequest } from "./authConfig";
import { decodeToken, isExpired } from "react-jwt";
import { InteractionStatus } from "@azure/msal-browser";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { marked } from "marked";
import { AiOutlineLoading } from "react-icons/ai";
import Swal from "sweetalert2";
import {
  FiThumbsUp,
  FiThumbsDown,
  FiCopy,
  FiCheck,
  FiAlertCircle,
  FiMenu,
  FiX,
} from "react-icons/fi";
import removeMd from "remove-markdown";

const SUGGESTIONS = [
  "Quantos dias tenho de férias? 🌽",
  "Como marcar uma ausência? ❓",
  "Como marcar uma despesa? 📄",
];

function App() {
  const { instance, inProgress } = useMsal();
  const [accessToken, setAccessToken] = useState(null);
  const [username, setUsername] = useState(null);
  const loginAttempted = useRef(false);
  const [apiURL, setApiURL] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [waveformValues, setWaveformValues] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState("");
  const [playingAudio, setPlayingAudio] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [loadingTTSIndex, setLoadingTTSIndex] = useState(null);
  const [loadingTTS, setLoadingTTS] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [commentErrors, setCommentErrors] = useState({});
  const [autoScroll, setAutoScroll] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const audioContextRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const commentInputRef = useRef(null);
  const textareaRef = useRef(null);

  const hasStartedChat = messages.length > 0;

  useEffect(() => {
    //messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    if (autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setAutoScroll(false);
    }

    //*********************************************************************************************************************************************
    // Autenticação
    //*********************************************************************************************************************************************
    const authenticate = async () => {
      const accounts = instance.getAllAccounts(); //verifica se já há contas autenticadas armazenadas na cache

      if (accounts.length > 0) {
        //se ja houver uma conta
        const account = accounts[0]; //escolhe a 1ª
        instance.setActiveAccount(account); // define esta como ativa

        try {
          //tentativa de adquirir um token de acesso, sem interação com o utilizador
          const resp = await instance.acquireTokenSilent({
            ...getTokenRequest(), //...loginRequest,
            account: account,
          });

          setAccessToken(resp.accessToken); // se for obtido, guarda o token (para depois enviarmos para o backend)

          const decoded = decodeToken(resp.idToken); // descodifica o idToken para obter informações do utilizador
          setUsername(decoded?.name || decoded?.preferred_username || "Guest"); //guarda o username ou mete "Guest"
          setApiURL(process.env.REACT_APP_BACKEND_API_URL); //redirect para o backend

          //se não tiver roles, força o logout
          if (!decoded?.roles || decoded?.roles.length === 0) {
            const result = await Swal.fire({
              icon: "error",
              title: "Sem permissões",
              text: "Neste momento não tens acesso à nossa ISA, por favor contacta o DSI para mais esclarecimentos.",
              allowOutsideClick: false, // impede fechar ao clicar fora
              allowEscapeKey: false, // impede fechar com ESC
              /*customClass: {
                //title: "swal-title-custom",
                //popup: "swal-popup-custom",
                //content: "swal-content-custom",
                confirmButton: "swal-button-custom",
              },*/
              confirmButtonText: "OK",
              confirmButtonColor: "#3085d6",
            });
            if (result.isConfirmed) {
              // ✅ Só redireciona após clicar em "OK"
              await instance.logoutRedirect();
            }
            return;
          }
        } catch (err) {
          //se o token não puder ser obtido silenciosamente, faz redirect para a página de login
          console.log(
            "Silent token acquisition failed. Falling back to redirect.",
            err
          );
          instance.acquireTokenRedirect(getTokenRequest());
        }
      } else {
        //se não houver contas
        //se, até ao momento, ninguém fez login e ainda não há login nesta sessão, inicia o login por redirecionamento
        if (!loginAttempted.current) {
          loginAttempted.current = true;
          instance.loginRedirect(getLoginRequest());
        }
      }
    };
    // só corre a função authenticate se nenhuma interação de login estiver em curso ou ainda não há username ou accessToken definidos
    if (inProgress === InteractionStatus.None && !username && !accessToken) {
      authenticate();
    }

    if (inProgress !== InteractionStatus.None) return;
  }, [messages, inProgress, instance, username, accessToken, autoScroll]);

  //*********************************************************************************************************************************************
  // Formatar resposta que vem do backend
  //*********************************************************************************************************************************************
  const formatMessage = (text) => {
    if (!text) return "";
    let formatted = text
      .replace(/^### (.*$)/gim, "<h3>$1</h3>") // ### → <h3>
      .replace(/^#### (.*)$/gim, "<u>$1</u>") // #### → texto sublinhado
      .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>") // ** → <strong>
      .replace(/\[doc\d+\]/g, "") //remove doc seguido de dígitos
      .replace(
        /\[([^\]]+)]\((https?:\/\/[^\s)]+(?:\?[^\s)]*)?)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      ) // [Texto](link) → <a>
      .replace(
    /\[([^\]]+)\]\(\s*(mailto:[^)\s]+)\s*\)/g,
    '<a href="$2">$1</a>'
  ) //email markdown [label](mailto:email)
       .replace(
    /(?<!href="mailto:)(\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b)/g,
    '<a href="mailto:$1">$1</a>'
  ) //email simples (mas ignorar se já estiverem dentro de <a ...>)
      .replace(/\n/g, "<br>") // \n → <br>
      .replace(/(<\/h[1-6]>)(<br\s*\/?>)+(?!\s*<h[1-6]>)/g, "$1"); // remove <br> após </hN>, exceto se vier outro título logo a seguir

      if(formatted.includes("https://isa.itsector.com/docsRedirect.html%3FidDoc%3D")){
        formatted = formatted.replace("%3F", "?").replace("%3D", "=");
      }

    const html = marked.parse(formatted); // Markdown para HTML
    const clean = DOMPurify.sanitize(html, {
      ADD_ATTR: ["target"],
    }); // Remove XSS

    return parse(clean);
  };

  //*********************************************************************************************************************************************
  // Forçar Login
  //*********************************************************************************************************************************************
  const scopesLogin = getLoginRequest();
  const forceLogin = {
    scopesLogin,
    prompt: "login", // força sempre login
  };

  //*********************************************************************************************************************************************
  // Obtém os chats (títulos)
  // Está visível na barra lateral esquerda (Último chats)
  //*********************************************************************************************************************************************
  const fetchChatHistory = async () => {
    if (!accessToken) return;

    if (isExpired(accessToken)) {
      instance.loginRedirect(forceLogin);
      return null;
    }

    try {
      const res = await fetch(`${apiURL}/chat/history`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) throw new Error("Erro ao carregar histórico");

      const data = await res.json();
      setChatHistory(data);
    } catch (err) {
      console.error("Erro ao buscar histórico:", err);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao carregar histórico.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, [accessToken, apiURL]);

  //*********************************************************************************************************************************************
  // Obtém o conteúdo de um chat dado o seu id
  // É visível quando se clica num título, na barra lateral esquerda. Obtém-se o histórico do chat em questão
  //*********************************************************************************************************************************************
  const loadChat = async (chatId) => {
    if (!accessToken) return;

    if (isExpired(accessToken)) {
      instance.loginRedirect(forceLogin);
      return null;
    }

    setCurrentChatId(chatId);

    try {
      const res = await fetch(`${apiURL}/chat/messages?chat_id=${chatId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!res.ok) throw new Error("Erro ao carregar mensagens do chat");

      const data = await res.json();
      const formattedMessages = data.map((msg) => ({
        sender: msg.role === "user" ? "user" : "bot",
        text: msg.conteudo,
        feedback: msg.feedback,
        comment: msg.comment,
        id: msg.id,
      }));

      setMessages(formattedMessages);
      setAutoScroll(true);
    } catch (err) {
      console.error("Erro ao carregar mensagens:", err);
      //alert("Erro ao carregar mensagens do chat.");
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao carregar mensagens do chat.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  //*********************************************************************************************************************************************
  // Criação de chat se não existir
  // Chamada ao endpoint do backend para obter resposta à pergunta feita (se existir histórico, vai inseri-lo no input)
  // Guarda/atualiza histórico
  //*********************************************************************************************************************************************
  const handleSubmit = async () => {
    if (!inputText.trim() || !accessToken) return;

    if (isExpired(accessToken)) {
      instance.loginRedirect(forceLogin);
      return;
    }
    let chatId = currentChatId;

    const userMessage = inputText.trim();
    setInputText("");
    setLoading(true);
    //setMessages((prev) => [...prev, { sender: "user", text: userMessage }]);

    // constrói o histórico completo antes de chamar o backend
    const historico = [...messages, { sender: "user", text: userMessage }];

    // Atualiza o estado do frontend (user message visível logo)
    setMessages(historico);

    try {
      // Criar novo chat (se ainda não existir)
      if (!chatId) {
        const titulo = userMessage;

        const res = await fetch(`${apiURL}/chat/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ titulo }),
        });

        if (!res.ok) throw new Error("Erro ao criar chat");
        const data = await res.json();
        chatId = data.chat_id;
        setCurrentChatId(chatId);
        await fetchChatHistory(); // atualiza sidebar
        setAutoScroll(true);
      }

      setAutoScroll(true);
      // Enviar pergunta para o bot para ser respondida
      const res = await fetch(`${apiURL}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          chat_id: chatId,
          messages: historico.slice(-10).map((m) => ({
            role: m.sender === "user" ? "user" : "bot",
            content: m.text,
          })),
        }),
      });

      if (!res.ok) throw new Error("Erro ao obter resposta");

      const data = await res.json();
      const botMessage = data.response || "Sem resposta do assistente.";

      // Atualizar mensagens no frontend
      setMessages((prev) => [...prev, { sender: "bot", text: botMessage }]);

      // Atualizar o chat no backend
      /*await fetch(`${apiURL}/chat/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          chat_id: chatId,
          messages: [
            { role: "user", conteudo: userMessage },
            { role: "bot", conteudo: botMessage },
          ],
        }),
      });*/
      //para obter o id para conseguir dar feedback/escrever comentários num novo chat
      loadChat(chatId);
      setAutoScroll(true);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      //alert("Erro ao comunicar com o servidor.");
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: err.message,
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setLoading(false);
    }
  };

  //*********************************************************************************************************************************************
  // Lê as respostas dadas
  // Carregar no ícone de som para ouvir a resposta
  //*********************************************************************************************************************************************
  const handleTTS = async (text, index) => {
    try {
      if (playingAudio && playingIndex === index) {
        playingAudio.pause();
        setPlayingAudio(null);
        setPlayingIndex(null);
        return;
      }

      if (playingAudio && !playingAudio.paused) {
        playingAudio.pause();
        setPlayingAudio(null);
        setPlayingIndex(null);
      }
      const validText = text.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1"); //remove o url para não ser lido

      if (!validText.trim() || !accessToken) return;

      if (isExpired(accessToken)) {
        instance.loginRedirect(forceLogin);
        return null;
      }

      setLoadingTTS("disabled");
      setLoadingTTSIndex(index);

      const response = await fetch(`${apiURL}/tts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ input: validText }),
      });
      if (!response.ok) throw new Error("Erro ao gerar áudio.");
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingAudio(null);
        setPlayingIndex(null);
      };
      setPlayingAudio(audio);
      setPlayingIndex(index);
      audio.play();
    } catch (err) {
      console.error("Erro ao gerar áudio:", err);
      //alert("Erro ao gerar áudio.");
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao gerar áudio.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setLoadingTTS("");
      setLoadingTTSIndex(null);
    }
  };

  //*********************************************************************************************************************************************
  // Para ouvir o que estamos a dizer e escrever a pergunta
  // Carregar no ícone do microfone e falar.
  // Clicar no ✅ quando acabar de falar para obter a pergunta.
  // Clicar no ❌ quando não se quer obter resultado (para parar -> NOTA: não se obtém a pergunta neste clique)
  //*********************************************************************************************************************************************
  const animateWaveform = () => {
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    const draw = () => {
      if (!analyser || !dataArray) return;

      analyser.getByteFrequencyData(dataArray);
      const normalized = Array.from(dataArray)
        .slice(0, 128)
        .map((n) => n / 255);
      setWaveformValues(normalized);
      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      source.connect(analyser);

      analyser.fftSize = 64;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      const recorder = new Recorder(audioCtx);
      await recorder.init(stream);
      await recorder.start();

      streamRef.current = stream;
      recorderRef.current = recorder;

      setRecording(true);
      setRecordingTime(0);
      animateWaveform();

      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      //alert("Erro ao iniciar gravação");
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao iniciar gravação",
        allowOutsideClick: false, // impede fechar ao clicar fora
        allowEscapeKey: false, // impede fechar com ESC
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  const stopRecording = async () => {
    try {
      if (!accessToken) return;

      if (isExpired(accessToken)) {
        instance.loginRedirect(forceLogin);
        return null;
      }

      clearInterval(recordingIntervalRef.current);
      cancelAnimationFrame(animationRef.current);
      setRecording(false);

      const { blob } = await recorderRef.current.stop();
      streamRef.current.getTracks().forEach((track) => track.stop());
      const formData = new FormData();
      formData.append(
        "file",
        new File([blob], "audio.wav", { type: "audio/wav" })
      );
      formData.append("model", "gpt-4o-transcribe");
      const response = await fetch(`${apiURL}/transcribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const result = await response.json();
      if (result.text) {
        setInputText(result.text);
        //handleSubmit();
      }
    } catch (err) {
      //alert("Erro ao transcrever áudio.");
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao transcrever áudio.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  //*********************************************************************************************************************************************
  // Gera um título com 25 caracteres
  //*********************************************************************************************************************************************
  const gerarTituloAutomatico = (texto, maxChar) => {
    const max = maxChar;
    let titulo = texto.trim().replace(/\s+/g, " ");
    return titulo.length > max ? titulo.substring(0, max) + "..." : titulo;
  };

  //*********************************************************************************************************************************************
  // Limpa as mensagens
  // Reset ao chatId
  //*********************************************************************************************************************************************
  const clearChat = async () => {
    setMessages([]);
    setCurrentChatId(null);
  };

  //*********************************************************************************************************************************************
  // Para atualizar o estado do feedback (thumbsup ou thumbsdown)
  // A partir do momento em que um dos ícones é clicado, esse resultado é gravado
  //*********************************************************************************************************************************************
  const updateFeedback = async (id, feedback, comment) => {
    try {
      if (id) {
        await fetch(`${apiURL}/chat/feedback`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            chHistory_id: id,
            feedback: feedback,
            comment: comment,
          }),
        });
        setMessages(
          messages.map((m) => (m.id === id ? { ...m, feedback, comment } : m))
        );

        // limpar erros
        setCommentErrors((prev) => ({ ...prev, [id]: null }));
      }
    } catch (err) {
      console.error("Erro ao guardar feedback/comentário:", err);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: err.message,
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  //*********************************************************************************************************************************************
  // Para copiar a resposta dada pelo bot
  //*********************************************************************************************************************************************
  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Erro ao copiar:", err);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: err.message,
        allowOutsideClick: false,
        allowEscapeKey: false,
        confirmButtonText: "OK",
        confirmButtonColor: "#3085d6",
      });
    }
  };

  //*********************************************************************************************************************************************
  // Força foco se:
  // - for dislike e tem erro
  // - estiver em edição
  //*********************************************************************************************************************************************
  useEffect(() => {
    if (!activeCommentId) return;

    const activeMsg = messages.find((m) => m.id === activeCommentId);

    if (
      (activeMsg?.feedback === false && commentErrors[activeMsg.id]) ||
      isEditing
    ) {
      commentInputRef.current?.focus();
    }
  }, [activeCommentId, commentErrors, isEditing, messages]);

  //*********************************************************************************************************************************************
  // Ajusta a height do textarea:
  // - texto é sempre visível, sem scroll e sem ser cortado
  //*********************************************************************************************************************************************
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [inputText]);

  //*********************************************************************************************************************************************
  // Sempre que o chat mudar, limpa o textarea
  //*********************************************************************************************************************************************
  useEffect(() => {
    setInputText("");
  }, [currentChatId]); // <-- dispara sempre que currentChatId mudar

  //*********************************************************************************************************************************************
  // Usado para mostrar ou não o disclaimer
  // Ir buscar a última mensagem e:
  // - verificar se tem o feedback preenchido e se o mesmo é diferente de false
  // OU
  // - verificar se o feedback é falso e não tem comentário
  //*********************************************************************************************************************************************
  const lastMessages = messages[messages.length - 1];
  const showDisclaimer =
    (!lastMessages?.feedback && lastMessages?.feedback !== false) ||
    (lastMessages?.feedback === false && !lastMessages?.comment);

  return (
    <div className="app-layout">
      {/* Botão para abrir/fechar a sidebar */}
      <button
        className={`menu-toggle ${isSidebarOpen ? "open" : ""}`}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? (
          <FiX size={24} className="close-button" />
        ) : (
          <FiMenu size={24} className="open-button" />
        )}
      </button>
      <aside className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        <div className="logo">
          <img src="/it-logo.png" alt="ITSector" />
        </div>
        <button
          className="new-chat"
          onClick={() => {
            setIsSidebarOpen(!isSidebarOpen);
            clearChat();
          }}
        >
          <IoChatbox className="chat-icon" /> Novo chat
        </button>
        <div className="history">
          <div className="section-title">Últimos chats</div>
          <ul className="latest-chats">
            {chatHistory.length === 0 ? (
              <div className="coming-soon">Sem histórico</div>
            ) : (
              chatHistory.map((chat) => (
                <li
                  key={chat.id}
                  className="chat-history-item"
                  title={chat.titulo}
                  style={{
                    cursor: "pointer",
                    fontSize: "14px",
                    marginBottom: "15px",
                  }}
                  onClick={() => {
                    loadChat(chat.id);
                    setIsSidebarOpen(!isSidebarOpen);
                  }}
                >
                  {gerarTituloAutomatico(chat.titulo, 25) || "Chat sem título"}
                </li>
              ))
            )}
          </ul>
        </div>
        <div className="user-info">{username}</div>
      </aside>
      <main className={`chat-main ${isSidebarOpen ? "sidebar-open" : ""}`}>
        {!hasStartedChat ? (
          <div className="chat-initial-wrapper">
            <h2>
              Olá {username ? username.split(" ")[0] : ""}, eu sou a ISA! 👋
            </h2>
            <p className="subtitle">Como é que eu te posso ajudar?</p>
            <div className="suggestions">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => setInputText(s)}>
                  {s}
                </button>
              ))}
            </div>
            {!recording ? (
              <div className="message-box message-box-initial">
                <textarea
                  id="searchArea"
                  ref={textareaRef}
                  placeholder="Pesquisa aqui qualquer coisa"
                  className="text-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <button
                  className="record-btn"
                  onClick={startRecording}
                  disabled={loading}
                >
                  <TiMicrophoneOutline />
                </button>
                <button
                  className="record-btn icon-button"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  <LuSendHorizontal />
                </button>
              </div>
            ) : (
              <div className="recording-bar recording-bar-initial">
                <button
                  className="recording-icon cancel"
                  onClick={() => {
                    setRecording(false);
                    cancelAnimationFrame(animationRef.current);
                    clearInterval(recordingIntervalRef.current);
                  }}
                >
                  <MdOutlineCancel />
                </button>
                <span className="recording-time">
                  {String(Math.floor(recordingTime / 60)).padStart(2, "0")}:
                  {String(recordingTime % 60).padStart(2, "0")}
                </span>
                <div className="waveform-placeholder">
                  {waveformValues.map((value, i) => {
                    return (
                      <div
                        key={i}
                        className="waveform-bar"
                        style={{
                          height: `${value * 100}%`,
                          maxHeight: "100%",
                        }}
                      />
                    );
                  })}
                </div>
                <button
                  className="recording-icon confirm"
                  onClick={stopRecording}
                >
                  <FaRegCheckCircle />
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="chat-header">
              <h2
                title={
                  chatHistory?.find((row) => row.id === currentChatId)
                    ?.titulo ?? "Chat novo"
                }
              >
                {chatHistory?.find((row) => row.id === currentChatId)
                  ? gerarTituloAutomatico(
                      chatHistory?.find((row) => row.id === currentChatId)
                        ?.titulo,
                      60
                    )
                  : "Chat novo"}
              </h2>
            </div>
            <div className="chat-box">
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.sender}`}>
                  <div className="message-text">{formatMessage(msg.text)}</div>
                  <div className="actions">
                    {/* Botão Copiar */}
                    {msg.sender === "bot" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          marginTop: "1rem",
                        }}
                      >
                        <button
                          className="copy-btn"
                          type="button"
                          onClick={() => handleCopy(removeMd(msg.text), index)} //para meter texto limpo
                        >
                          {copiedIndex === index ? (
                            <FiCheck size={18} />
                          ) : (
                            <FiCopy size={18} />
                          )}
                        </button>
                      </div>
                    )}

                    {/*/ voz - ouvir resposta */}
                    {msg.sender === "bot" && !msg.initial && (
                      <button
                        className="tts-btn"
                        onClick={() => handleTTS(msg.text, index)}
                        title="Ouvir resposta"
                        disabled={loadingTTS}
                        style={{ color: "#999" }}
                      >
                        {loadingTTSIndex === index ? (
                          <AiOutlineLoading className="spinner" />
                        ) : playingIndex === index && playingAudio ? (
                          <CiStop1 />
                        ) : (
                          <HiOutlineSpeakerWave />
                        )}
                      </button>
                    )}

                    {/* feedback */}
                    {msg.sender === "bot" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          marginTop: "1rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <button
                          type="button"
                          //disabled={activeCommentId && activeCommentId !== msg.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // guarda like e abre input
                            updateFeedback(msg.id, true, msg.comment || "");
                            setActiveCommentId(msg.id);
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            borderRadius: "50%",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.2rem",
                            color: msg.feedback === true ? "#0fbcf9" : "#999",
                          }}
                        >
                          <FiThumbsUp size={18} />
                        </button>
                        <button
                          type="button"
                          //disabled={activeCommentId && activeCommentId !== msg.id}
                          onClick={() => {
                            // dislike abre caixa obrigatória
                            setActiveCommentId(msg.id);
                            setMessages(
                              messages.map((m) =>
                                m.id === msg.id ? { ...m, feedback: false } : m
                              )
                            );
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            borderRadius: "50%",
                            transition: "all 0.2s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.2rem",
                            color: msg.feedback === false ? "#ff6b6b" : "#999",
                          }}
                        >
                          <FiThumbsDown size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="coments">
                    {/* comentário (apenas aparece se tiver like/dislike) */}
                    {activeCommentId === msg.id && (
                      <div className="inputC">
                        <textarea
                          id="comentsInput"
                          ref={
                            activeCommentId === msg.id ? commentInputRef : null
                          }
                          type="text"
                          placeholder={
                            msg.feedback
                              ? "Deixa aqui o teu comentário"
                              : "O que foi insatisfatório na mensagem?"
                          }
                          className="text-input"
                          value={msg.comment || ""}
                          onChange={(e) => {
                            setMessages(
                              messages.map((m) =>
                                m.id === msg.id
                                  ? { ...m, comment: e.target.value }
                                  : m
                              )
                            );
                            // limpa o erro assim que o usuário começa a escrever
                            if (
                              e.target.value.trim() &&
                              commentErrors[msg.id]
                            ) {
                              setCommentErrors((prev) => ({
                                ...prev,
                                [msg.id]: null,
                              }));
                            }
                            const el = e.target;
                            el.style.height = "auto"; // reset
                            el.style.height = `${el.scrollHeight}px`; // ajusta
                          }}
                          onBlur={() => {
                            // se feedback for negativo e não tiver texto
                            if (
                              msg.feedback === false &&
                              !msg.comment?.trim()
                            ) {
                              setCommentErrors((prev) => ({
                                ...prev,
                                [msg.id]:
                                  "Este campo é de preenchimento obrigatório",
                              }));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (
                                msg.feedback === false &&
                                !msg.comment?.trim()
                              ) {
                                setCommentErrors((prev) => ({
                                  ...prev,
                                  [msg.id]:
                                    "Este campo é de preenchimento obrigatório",
                                }));
                                return;
                              }
                              updateFeedback(
                                msg.id,
                                msg.feedback,
                                msg.comment || ""
                              );
                              setActiveCommentId(null);
                            }
                          }}
                          onFocus={(e) => {
                            const el = e.target;
                            el.style.height = "auto";
                            el.style.height = `${el.scrollHeight}px`;
                          }}
                        />

                        <button
                          className="send-btn"
                          onClick={() => {
                            if (
                              msg.feedback === false &&
                              !msg.comment?.trim()
                            ) {
                              setCommentErrors((prev) => ({
                                ...prev,
                                [msg.id]:
                                  "Este campo é de preenchimento obrigatório",
                              }));
                              return;
                            }
                            updateFeedback(
                              msg.id,
                              msg.feedback,
                              msg.comment || ""
                            );
                            //fechar depois de guardar
                            setActiveCommentId(null);
                            setIsEditing(false);
                          }}
                          style={{
                            right: msg.feedback === true ? "3.5rem" : "1.5rem",
                          }}
                        >
                          <LuSendHorizontal size={18} />
                        </button>
                        {msg.feedback === true && (
                          <button
                            className="close-btn"
                            onClick={() => {
                              setActiveCommentId(null);
                              setIsEditing(false);
                            }}
                          >
                            <LuX size={18} />
                          </button>
                        )}
                      </div>
                    )}
                    {/* comentário guardado/editar */}
                    {msg.comment && activeCommentId !== msg.id && (
                      <div className="savedC">
                        <span>{msg.comment}</span>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => {
                            setActiveCommentId(msg.id);
                            setIsEditing(true); // o useEffect vai forçar o foco
                          }}
                        >
                          <LuPencil size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Mensagem de erro obrigatória */}
                  {commentErrors[msg.id] && (
                    <p
                      style={{
                        color: "red",
                        marginTop: "0.50rem",
                        fontSize: "13px",
                      }}
                    >
                      {commentErrors[msg.id]}
                    </p>
                  )}
                </div>
              ))}

              {loading && (
                <div className="typing-indicator">A ISA está a escrever...</div>
              )}
              <div ref={messagesEndRef} />
              {showDisclaimer && (
                <div className="disclaimer">
                  <p className="disclaimer-content">
                    <FiAlertCircle className="disclaimer-icon" size={18} />
                    <span className="disclaimer-text">
                      Neste momento encontro-me em fase de certificação, pelo
                      que as minhas respostas podem apresentar algumas
                      incoerências. Por favor, dá-me o teu feedback para me
                      ajudar a melhorar! 😊
                    </span>
                  </p>
                </div>
              )}
            </div>
            {recording ? (
              <div className="recording-bar">
                <button
                  className="recording-icon cancel"
                  onClick={() => {
                    setRecording(false);
                    cancelAnimationFrame(animationRef.current);
                    clearInterval(recordingIntervalRef.current);
                  }}
                >
                  <MdOutlineCancel />
                </button>
                <span className="recording-time">
                  {String(Math.floor(recordingTime / 60)).padStart(2, "0")}:
                  {String(recordingTime % 60).padStart(2, "0")}
                </span>
                <div className="waveform-placeholder">
                  {waveformValues.map((value, i) => {
                    return (
                      <div
                        key={i}
                        className="waveform-bar"
                        style={{
                          height: `${value * 100}%`,
                          maxHeight: "100%",
                        }}
                      />
                    );
                  })}
                </div>
                <button
                  className="recording-icon confirm"
                  onClick={stopRecording}
                >
                  <FaRegCheckCircle />
                </button>
              </div>
            ) : (
              <div className="message-box">
                <textarea
                  id="searchArea"
                  ref={textareaRef}
                  type="text"
                  placeholder="Pesquisa aqui qualquer coisa"
                  className="text-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                />
                <button
                  className="record-btn"
                  onClick={startRecording}
                  disabled={loading}
                >
                  <TiMicrophoneOutline />
                </button>
                <button
                  className="record-btn icon-button"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  <LuSendHorizontal />
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
